import {
  GqlDispensaries,
  GqlDispensariesForChainQueryVariables,
  useDispensariesForChainQuery,
  useOrdersForConsumerInChainQuery,
  GqlOrdersForConsumerInChainQueryVariables,
  StringSortDirections,
} from 'types/graphql';
import { NetworkStatus } from 'apollo-client';

import useUser from 'src/hooks/use-user';
import { GraphqlError } from 'src/errors';
import { ConsumerUser } from '../../../account.types';
import { Orders } from './types';

type UseDispensariesForChainParams = {
  chain?: string | null;
};

type UseOrdersForConsumerParams = {
  dispensary: GqlDispensaries | null;
  skip?: boolean;
};

type UseVariablesForOrdersForConsumerParams = {
  dispensary: GqlDispensaries | null;
  user: ConsumerUser;
  dispensaryIdsForChain: string[] | [];
};

export type UseOrdersForConsumerReturn = {
  loading: boolean;
  fetchMore: any;
  totalCount: number;
  orders: Orders;
};

function useVariablesForChainDispensaries({
  chain,
}: UseDispensariesForChainParams): GqlDispensariesForChainQueryVariables | undefined {
  if (!chain) {
    return undefined;
  }

  return {
    dispensaryFilter: {
      type: `Dispensary`,
      chain,
      activeOnly: true,
      includeStealthMode: true,
    },
  };
}

function useDispensariesForChainIds({ chain }: UseDispensariesForChainParams): string[] | [] {
  const variables = useVariablesForChainDispensaries({ chain });

  const { data, error } = useDispensariesForChainQuery({
    variables,
    skip: !variables,
  });

  if (error) {
    throw new GraphqlError(error);
  }

  const dispoIds = data?.filteredDispensaries?.map((dispo) => dispo?.id ?? '').filter((id) => id !== '') ?? [];

  return dispoIds;
}

function useVariablesForOrdersForConsumer({
  dispensary,
  user,
  dispensaryIdsForChain,
}: UseVariablesForOrdersForConsumerParams): GqlOrdersForConsumerInChainQueryVariables {
  const userId = user.id;
  const dispensaryId = dispensary?.id;

  return {
    ordersSort: { sortBy: `createdAt`, sortDirection: StringSortDirections.desc },
    ordersFilter: {
      userId,
      dispensaryId,
      dispensaryIds: [...dispensaryIdsForChain],
    },
    ordersPagination: { limit: 5 },
  };
}

export function useOrdersForConsumer({
  dispensary,
  skip: externalSkip = false,
}: UseOrdersForConsumerParams): UseOrdersForConsumerReturn {
  const user = useUser();
  const dispensaryIdsForChain = useDispensariesForChainIds({ chain: dispensary?.chain });
  const variables = useVariablesForOrdersForConsumer({ dispensary, user, dispensaryIdsForChain });
  const skip = externalSkip || !variables.ordersFilter.userId;

  const { data, loading, error, networkStatus, fetchMore } = useOrdersForConsumerInChainQuery({
    variables,
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
    skip,
  });

  if (error) {
    throw new GraphqlError(error);
  }

  const isLoading = skip || loading || networkStatus === NetworkStatus.fetchMore;
  const totalCount = data?.filteredOrders?.queryInfo?.totalCount ?? 0;
  const orders =
    data?.filteredOrders?.orders && Array.isArray(data.filteredOrders.orders) ? data.filteredOrders.orders : [];

  return {
    fetchMore,
    loading: isLoading,
    orders,
    totalCount,
  };
}

export const queryMoreOrders = (fetchMore, orders): void => {
  fetchMore({
    variables: {
      ordersPagination: {
        limit: 5,
        offset: orders.length,
      },
    },
    updateQuery: (prev, { fetchMoreResult }) => {
      if (!fetchMoreResult) {
        return prev;
      }

      /* eslint-disable @typescript-eslint/naming-convention */
      const { __typename, queryInfo } = fetchMoreResult.filteredOrders;

      return {
        filteredOrders: {
          __typename,
          queryInfo,
          orders: [...orders, ...fetchMoreResult.filteredOrders.orders],
        },
      };
    },
  });
};
